// Modal de edição de permissões de um role (PO1-2044).
// Padrão de Modal: modal-align-top + fade={false} + div header customizado
// (referência: EditCotacaoModal em Empresa/ListagemCotacoes).

import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";

import {
  getPermissionsTree,
  getRolePermissions,
  updateRolePermissions,
} from "services/rolesApi";
import { useToast } from "hooks/toast";
import PermissionTreeView, {
  collectSubtreeNames,
} from "./PermissionTreeView";

export default function EditRolePermissionsModal({ isOpen, role, onClose, onSaved }) {
  const [tree, setTree] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  // Carrega árvore + grants atuais quando o modal abre
  useEffect(() => {
    if (!isOpen || !role) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getPermissionsTree(), getRolePermissions(role.Id)])
      .then(([treeData, grantsData]) => {
        if (cancelled) return;
        setTree(treeData);
        setSelected(new Set(grantsData.permissions || []));
      })
      .catch((err) => {
        console.error("[EditRolePermissionsModal] load:", err);
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Não foi possível carregar permissões",
          icon: "tim-icons icon-alert-circle-exc",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, role, addToast]);

  const handleToggle = useCallback((name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  // Cascade: clica num pai → marca/desmarca o subárvore inteira (UI). A
  // gravação no backend continua sendo string-a-string.
  const handleToggleSubtree = useCallback((names, value) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const name of names) {
        if (value) next.add(name);
        else next.delete(name);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!tree) return;
    setSelected(new Set(collectSubtreeNames(tree)));
  }, [tree]);

  const handleDeselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleSave = useCallback(async () => {
    if (!role) return;
    setSaving(true);
    try {
      const result = await updateRolePermissions(role.Id, Array.from(selected));
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: `${result.added.length} adicionada(s), ${result.removed.length} removida(s).`,
        icon: "tim-icons icon-check-2",
      });
      if (onSaved) onSaved(result);
      onClose();
    } catch (err) {
      console.error("[EditRolePermissionsModal] save:", err);
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Erro ao salvar permissões";
      const invalid = err.response && err.response.data && err.response.data.invalid;
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: invalid
          ? `${message}: ${invalid.join(", ")}`
          : message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [role, selected, addToast, onSaved, onClose]);

  if (!role) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#141b2e",
          borderBottom: "1px solid #2b3553",
          padding: "16px 20px",
        }}
      >
        <span style={{ color: "#f5f7fb", fontWeight: 600, fontSize: 15 }}>
          Editar permissões — {role.DisplayName || role.Name}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#f5f7fb",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div
          style={{
            background: "rgba(29,140,248,0.08)",
            borderLeft: "3px solid #1d8cf8",
            padding: "10px 14px",
            color: "#8fa3c8",
            fontSize: 12,
            marginBottom: 16,
          }}
        >
          Esta tela gerencia apenas permissões do <strong>Hub</strong> (
          <code>hub.*</code>). Permissões do FLOW continuam sendo gerenciadas no
          FLOW. Cada permissão é gravada individualmente no banco — o cascade
          ao clicar no pai é apenas atalho de UI.
        </div>

        {!loading && tree && (
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <span style={{ color: "#8fa3c8", fontSize: 12 }}>
              {selected.size} permissão(ões) selecionada(s)
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                color="info"
                size="sm"
                outline
                onClick={handleSelectAll}
                disabled={saving}
              >
                Marcar todas
              </Button>
              <Button
                color="secondary"
                size="sm"
                outline
                onClick={handleDeselectAll}
                disabled={saving}
              >
                Desmarcar todas
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: "#8fa3c8", textAlign: "center", padding: 20 }}>
            Carregando…
          </div>
        ) : (
          <PermissionTreeView
            tree={tree}
            selectedNames={selected}
            onToggle={handleToggle}
            onToggleSubtree={handleToggleSubtree}
          />
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="success" onClick={handleSave} disabled={saving || loading}>
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
